import { taskConstant } from 'actionTypes';
import { TEMPORARY } from 'helpers/constant';
import { TASK_FILE_STATUS, TASK_STATUS, VIEW_MODE } from 'helpers/enum';
import { Reducer } from 'redux';
import { TaskActionTypes } from 'types/actions/task';
import { CurrentTask, Task, TemporaryTask } from 'types/reducers/task';

interface TaskState {
  tasks: {
    data: Task[];
    total_item: number;
    total_page: number;
    current_page: number;
  };
  status: TASK_STATUS;
  process_task_id: string;
  processingStreamingChunkStatusTask: boolean;
  current: CurrentTask;
  temporary_task: TemporaryTask;
  viewMode: VIEW_MODE;
  isLimitReached: boolean;
}

const initialState: TaskState = {
  tasks: {
    data: [],
    total_item: 0,
    total_page: 0,
    current_page: 0,
  },
  status: TASK_STATUS.IDLE,
  process_task_id: '',
  processingStreamingChunkStatusTask: false,
  current: {
    task_id: '',
    type: '',
    data: [],
    selectedFiles: [],
    initialSelectedFiles: [],
    isWaitingProcessFileUpload: false,
    answerChunks: {
      order: -1,
      part_of_answer: '',
      question_id: '',
      summary_id: '',
    },
    setTimeOutFinalAnswerTask: false,
  },
  temporary_task: {
    task_id: '',
    data: [],
    selectedFiles: [],
  },
  viewMode: VIEW_MODE.CHAT,
  isLimitReached: false,
};

const taskReducer: Reducer<TaskState, TaskActionTypes> = (
  state = initialState,
  action: TaskActionTypes
) => {
  switch (action.type) {
    case taskConstant.UPDATE_TASKS: {
      return {
        ...state,
        tasks: action.payload,
      };
    }
    case taskConstant.UPDATE_STATUS_TASK: {
      return {
        ...state,
        status: action.payload,
      };
    }
    case taskConstant.UPDATE_CURRENT_QUESTION_TASK: {
      const tempData = [...state.current.data];
      const latestMessage = tempData[tempData.length - 1];
      latestMessage.answer = '';
      tempData[tempData.length - 1] = {
        ...latestMessage,
        ...action.payload,
      };
      return {
        ...state,
        current: {
          ...state.current,
          data: tempData,
        },
      };
    }
    case taskConstant.UPDATE_CURRENT_TASK: {
      return {
        ...state,
        current: {
          ...state.current,
          ...action.payload,
        },
      };
    }
    case taskConstant.UPDATE_PROCESS_TASK_ID: {
      return {
        ...state,
        process_task_id: action.payload,
      };
    }
    case taskConstant.UPDATE_TEMPORARY_TASK_ID: {
      return {
        ...state,
        temporary_task: {
          ...state.temporary_task,
          task_id: action.payload,
        },
      };
    }
    case taskConstant.UPDATE_CURRENT_TASK_DATA: {
      // Selected files is files of last message
      const selectedFiles =
        action.payload[action.payload.length - 1]?.files?.map((file) => ({
          ...file,
          task_file_status: TASK_FILE_STATUS.SAVED,
        })) ?? [];

      // In case of empty message task
      if (action.payload.length === 1 && !action.payload[0].question) {
        return {
          ...state,
          current: {
            ...state.current,
            data: [],
            selectedFiles,
          },
        };
      }

      return {
        ...state,
        current: {
          ...state.current,
          data: action.payload,
          selectedFiles,
        },
      };
    }
    case taskConstant.CLEAR_CURRENT_TASK_SELECTED_FILES: {
      return {
        ...state,
        current: {
          ...state.current,
          selectedFiles: [],
        },
      };
    }
    case taskConstant.CLEAR_TEMPORARY_TASK: {
      return {
        ...state,
        temporary_task: {
          task_id: '',
          data: [],
          selectedFiles: [],
        },
      };
    }
    case taskConstant.LOAD_TEMPORARY_TASK_DATA: {
      return {
        ...state,
        current: {
          ...state.current,
          selectedFiles: state.temporary_task.selectedFiles,
        },
      };
    }
    case taskConstant.UPDATE_NEW_QUESTION_TASK: {
      return {
        ...state,
        current: {
          ...state.current,
          data: [...state.current.data, action.payload],
        },
      };
    }

    case taskConstant.ADD_NEW_TASK: {
      return {
        ...state,
        tasks: {
          ...state.tasks,
          data: [action.payload, ...state.tasks.data],
          total_item: state.tasks.total_item + 1,
        },
      };
    }
    case taskConstant.UPDATE_NEW_ANSWER_TASK: {
      // Only update when match current task
      if (action.payload?.id === state.current.task_id) {
        const newChatData = [...state.current.data];
        const newMesssage = {
          ...action.payload,
          updated_at: action.payload.timestamp,
        };
        const lastMessage = newChatData[newChatData?.length - 1];
        newChatData[newChatData.length - 1] = {
          ...lastMessage,
          ...newMesssage,
        };
        return {
          ...state,
          current: {
            ...state.current,
            data: newChatData,
          },
          status: TASK_STATUS.IDLE,
        };
      }
      return { ...state, status: TASK_STATUS.IDLE };
    }

    case taskConstant.UPDATE_NEW_STREAMING_ANSWER_CHUNKS_TASK: {
      if (
        action.payload?.summary_id === state.current.task_id &&
        action.payload.order > state.current.answerChunks.order
      ) {
        return {
          ...state,
          current: {
            ...state.current,
            answerChunks: {
              part_of_answer: action.payload.part_of_answer,
              order: action.payload.order,
              summary_id: action.payload.summary_id,
              question_id: action.payload.question_id,
            },
          },
        };
      }

      return state;
    }

    case taskConstant.SET_TIMEOUT_FOR_FINAL_ANSWER_TASK: {
      return {
        ...state,
        current: {
          ...state.current,
          setTimeOutFinalAnswerTask: action.payload,
        },
      };
    }

    case taskConstant.UPDATE_PROCESSING_STREAMING_CHUNK_STATUS_TASK: {
      return {
        ...state,
        processingStreamingChunkStatusTask: action.payload,
      };
    }

    case taskConstant.CLEAR_STREAMING_ANSWER_CHUNKS_TASK: {
      return {
        ...state,
        current: {
          ...state.current,
          answerChunks: {
            order: -1,
            part_of_answer: '',
            question_id: '',
            summary_id: '',
          },
        },
      };
    }

    case taskConstant.UPDATE_LIMIT_STATUS_TASK: {
      return {
        ...state,
        isLimitReached: action.payload,
      };
    }
    case taskConstant.ADD_SELECTED_FILE: {
      const isNewFileSeleted = state.current.selectedFiles.some(
        (file) => file.id === action.payload.id
      );
      const newSelectedFiles = [
        ...state.current.selectedFiles,
        ...(!isNewFileSeleted ? [action.payload] : []),
      ];

      return {
        ...state,
        current: {
          ...state.current,
          selectedFiles: newSelectedFiles,
        },
        temporary_task: {
          ...state.temporary_task,
          ...(state.current.type === TEMPORARY && { selectedFiles: newSelectedFiles }),
        },
      };
    }
    case taskConstant.UPDATE_SELECTED_FILE: {
      if (
        action.payload.data.task_file_status &&
        [TASK_FILE_STATUS.PROCESSED, TASK_FILE_STATUS.UPLOADED].includes(
          action.payload.data.task_file_status
        )
      ) {
        const isUpdateTemporaryTask = action.payload.isTemporary;
        const isCurrentTemporaryTask = state.current.type === TEMPORARY;
        let updateFileIndex;

        if (isUpdateTemporaryTask) {
          updateFileIndex = state.temporary_task.selectedFiles.findIndex(
            (file) => file.task_file_status === TASK_FILE_STATUS.UPLOADED
          );

          const newSelectedFiles = [...state.temporary_task.selectedFiles];
          newSelectedFiles[updateFileIndex] = {
            ...newSelectedFiles[updateFileIndex],
            ...action.payload.data,
          };

          return {
            ...state,
            current: {
              ...state.current,
              ...(isCurrentTemporaryTask && {
                selectedFiles: newSelectedFiles,
              }),
            },
            temporary_task: {
              ...state.temporary_task,
              selectedFiles: newSelectedFiles,
            },
          };
        }

        updateFileIndex = state.current.selectedFiles.findIndex(
          (file) => file.task_file_status === TASK_FILE_STATUS.UPLOADED
        );

        const newSelectedFiles = [...state.current.selectedFiles];
        newSelectedFiles[updateFileIndex] = {
          ...newSelectedFiles[updateFileIndex],
          ...action.payload.data,
        };

        return {
          ...state,
          current: {
            ...state.current,
            selectedFiles: newSelectedFiles,
          },
        };
      }
      return state;
    }
    case taskConstant.REMOVE_SELECTED_FILE: {
      const newSelectedFiles = state.current.selectedFiles.filter(
        (file) => file.id !== action.payload.id
      );
      return {
        ...state,
        current: {
          ...state.current,
          selectedFiles: newSelectedFiles,
        },
      };
    }
    case taskConstant.UPDATE_INITIAL_SELECTED_FILES: {
      return {
        ...state,
        current: {
          ...state.current,
          initialSelectedFiles: state.current.selectedFiles,
        },
      };
    }
    case taskConstant.RESET_DATA: {
      return initialState;
    }
    default:
      return state;
  }
};

export default taskReducer;
